import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";

/**
 * Circle avatar with upload / replace / remove controls.
 *
 * Props:
 * - userId: the current user's id (used to build the download URL)
 * - fallback: string shown when there is no image (typically initials or the user's name)
 * - onChanged: called after a successful upload/delete with an integer "cacheBust"
 * - size: pixel size of the circle (default 96)
 */
export default function AvatarUpload({ userId, fallback, onChanged, size = 96 }) {
  const inputRef = useRef(null);
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cacheBust, setCacheBust] = useState(() => Date.now());
  const [hasImage, setHasImage] = useState(false);

  useEffect(() => {
    if (!userId) return;
    // HEAD probe to know whether an avatar exists (avoids showing broken img)
    fetch(`${BACKEND_URL}/api/profile/avatar/${userId}`, { credentials: "include", method: "GET" })
      .then((r) => setHasImage(r.ok))
      .catch(() => setHasImage(false));
  }, [userId, cacheBust]);

  const src = userId ? `${BACKEND_URL}/api/profile/avatar/${userId}?v=${cacheBust}` : "";

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large — max 2 MB");
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG or WEBP images allowed");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/profile/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      const stamp = Date.now();
      setCacheBust(stamp);
      setHasImage(true);
      toast.success("Profile picture updated");
      onChanged?.(stamp);
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!hasImage) return;
    setBusy(true);
    try {
      await api.delete("/profile/avatar");
      setHasImage(false);
      const stamp = Date.now();
      setCacheBust(stamp);
      toast.success("Profile picture removed");
      onChanged?.(stamp);
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const initials = String(fallback || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

  return (
    <div className="flex items-center gap-4" data-testid="avatar-upload-wrapper">
      <div
        className="relative rounded-full overflow-hidden bg-zinc-100 border-2 border-white ring-2 ring-zinc-200 grid place-items-center"
        style={{ width: size, height: size }}
      >
        {hasImage ? (
          <img
            src={src}
            alt="Profile"
            data-testid="avatar-image"
            className="w-full h-full object-cover"
            onError={() => setHasImage(false)}
          />
        ) : (
          <span
            className="font-display font-black text-zinc-500"
            style={{ fontSize: Math.round(size * 0.35) }}
            data-testid="avatar-fallback"
          >
            {initials}
          </span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/40 grid place-items-center">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Button
          type="button"
          onClick={onPick}
          disabled={busy}
          data-testid="avatar-upload-btn"
          variant="outline"
          size="sm"
          className="border-blue-200 text-[#002FA7] hover:bg-blue-50"
        >
          <Camera className="h-3.5 w-3.5 mr-1.5" />
          {hasImage ? "Change photo" : "Upload photo"}
        </Button>
        {hasImage && (
          <Button
            type="button"
            onClick={onRemove}
            disabled={busy}
            data-testid="avatar-remove-btn"
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-800 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
          </Button>
        )}
        <p className="text-xs text-zinc-500 tag-mono">JPG · PNG · WEBP · max 2 MB</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="avatar-file-input"
      />
    </div>
  );
}
